import type { Key } from 'react';
import React, { ReactNode, useCallback } from 'react';
import type { SortOrder } from 'antd/es/table/interface';

import type { MadeColumnType } from '../hooks/useMakeColumns';
import type { TableProps } from '../types';
import { useFilterDropdown } from '../hooks/useFilterDropdown';
import { TableAction } from './types';
import { HeaderCell } from './HeaderCell';
import type { LocalSortState } from './useLocalSort';

interface TableHeaderProps {
    headerScrollRef: React.RefObject<HTMLDivElement | null>;
    displayColumns: MadeColumnType[];
    checkboxColumnTitle?: ReactNode;
    buildAllFilters: (overrideKey: string, overrideValue: Key[] | null) => Record<string, Key[] | null>;
    tableRef: React.RefObject<HTMLDivElement | null>;
    onTableChange?: TableProps<any>['onChange'];
    resetScroll: () => void;
    dataSource: readonly any[];
    localSort: LocalSortState | null;
    onSortChange: (sort: LocalSortState) => void;
}

export function TableHeader({
    headerScrollRef,
    displayColumns,
    checkboxColumnTitle,
    buildAllFilters,
    tableRef,
    onTableChange,
    resetScroll,
    dataSource,
    localSort,
    onSortChange,
}: TableHeaderProps) {
    const { filterDropdownOpenKey, setFilterDropdownOpenKey, filterTriggerRef, renderFilterDropdown } =
        useFilterDropdown({
            buildAllFilters,
            onTableChange,
            dataSource,
            resetScroll,
            tableRef,
            columns: displayColumns,
        });

    const handleSortClick = useCallback(
        (columnKey: string, order: SortOrder) => {
            onSortChange({ columnKey, order });
            onTableChange?.(
                {},
                {},
                { column: { key: columnKey }, order },
                {
                    action: TableAction.Sort,
                    currentDataSource: dataSource as any[],
                },
            );
            resetScroll();
        },
        [onSortChange, onTableChange, dataSource, resetScroll],
    );

    const getSortOrderForColumn = useCallback(
        (col: MadeColumnType): SortOrder | undefined => {
            const columnKey = String(col.key);
            const fromColumn = col.sortOrder;
            if (fromColumn != null) return fromColumn;
            if (localSort?.columnKey === columnKey) return localSort.order;
            return col.defaultSortOrder;
        },
        [localSort],
    );

    return (
        <>
            <div
                ref={headerScrollRef}
                className="virtual_table_header_scroll"
                style={{ paddingRight: 'var(--scrollbar-width)' }}
            >
                <div className="virtual_table_header_row">
                    {displayColumns.map((col, colIndex) => (
                        <HeaderCell
                            key={`${col.key}:${colIndex}`}
                            col={col}
                            colIndex={colIndex}
                            checkboxColumnTitle={checkboxColumnTitle}
                            filterDropdownOpenKey={filterDropdownOpenKey}
                            filterTriggerRef={filterTriggerRef}
                            setFilterDropdownOpenKey={setFilterDropdownOpenKey}
                            getSortOrderForColumn={getSortOrderForColumn}
                            handleSortClick={handleSortClick}
                        />
                    ))}
                </div>
            </div>
            {renderFilterDropdown()}
        </>
    );
}
